import {
  CanvassingSessionActivityKind,
  CanvassingSessionCaptureStatus,
  CanvassingSessionCaptureStatusFilter,
  CanvassingSessionDoorVisitStatus,
  CanvassingSessionListSort,
  CanvassingSessionPipelineJobStatus,
  CanvassingSessionPipelineStage,
  CanvassingSessionProcessingStatus,
  SessionTelemetryAudioState,
  SessionTelemetryCategory,
  SessionTelemetryConnectivity,
  SessionTelemetryEventType,
  SessionTelemetryFailureReason,
  SessionTelemetryGpsState,
  SessionTelemetryOutcome,
  SessionTelemetrySource,
} from '../../graphql/__generated__/graphql';

const captureStatusLabels: Record<CanvassingSessionCaptureStatus, string> = {
  [CanvassingSessionCaptureStatus.Active]: 'Active',
  [CanvassingSessionCaptureStatus.Ended]: 'Ended',
  [CanvassingSessionCaptureStatus.Unknown]: 'Unknown',
};

const captureStatusFilterLabels: Record<CanvassingSessionCaptureStatusFilter, string> = {
  [CanvassingSessionCaptureStatusFilter.Active]: captureStatusLabels[CanvassingSessionCaptureStatus.Active],
  [CanvassingSessionCaptureStatusFilter.Ended]: captureStatusLabels[CanvassingSessionCaptureStatus.Ended],
};

const processingStatusLabels: Record<CanvassingSessionProcessingStatus, string> = {
  [CanvassingSessionProcessingStatus.Active]: 'Active',
  [CanvassingSessionProcessingStatus.AudioPending]: 'Audio pending',
  [CanvassingSessionProcessingStatus.Failed]: 'Failed',
  [CanvassingSessionProcessingStatus.Merged]: 'Merged',
  [CanvassingSessionProcessingStatus.Partial]: 'Partial',
  [CanvassingSessionProcessingStatus.Segmented]: 'Segmented',
  [CanvassingSessionProcessingStatus.Submitted]: 'Submitted',
  [CanvassingSessionProcessingStatus.Unknown]: 'Unknown',
};

const listSortLabels: Record<CanvassingSessionListSort, string> = {
  [CanvassingSessionListSort.Newest]: 'Newest',
  [CanvassingSessionListSort.Oldest]: 'Oldest',
};

const activityKindLabels: Record<CanvassingSessionActivityKind, string> = {
  [CanvassingSessionActivityKind.FirstAudioConfirmed]: 'First audio confirmed',
  [CanvassingSessionActivityKind.FirstGpsReceived]: 'First GPS received',
  [CanvassingSessionActivityKind.FirstPass1Submitted]: 'First pass 1 submitted',
  [CanvassingSessionActivityKind.FirstPass2Submitted]: 'First pass 2 submitted',
  [CanvassingSessionActivityKind.FirstVisitCreated]: 'First door visit created',
  [CanvassingSessionActivityKind.LatestAudioConfirmed]: 'Latest audio confirmed',
  [CanvassingSessionActivityKind.LatestGpsReceived]: 'Latest GPS received',
  [CanvassingSessionActivityKind.LatestPass1Submitted]: 'Latest pass 1 submitted',
  [CanvassingSessionActivityKind.LatestPass2Submitted]: 'Latest pass 2 submitted',
  [CanvassingSessionActivityKind.LatestVisitCreated]: 'Latest door visit created',
  [CanvassingSessionActivityKind.PipelineStageFirstCreated]: 'Pipeline stage first created',
  [CanvassingSessionActivityKind.PipelineStageLatestUpdated]: 'Pipeline stage latest updated',
  [CanvassingSessionActivityKind.SessionEnded]: 'Session ended',
  [CanvassingSessionActivityKind.SessionStarted]: 'Session started',
};

const pipelineStageLabels: Record<CanvassingSessionPipelineStage, string> = {
  [CanvassingSessionPipelineStage.Merge]: 'Merge',
  [CanvassingSessionPipelineStage.Segment]: 'Segment',
  [CanvassingSessionPipelineStage.Submit]: 'Submit',
  [CanvassingSessionPipelineStage.Unknown]: 'Unknown',
};

const pipelineJobStatusLabels: Record<CanvassingSessionPipelineJobStatus, string> = {
  [CanvassingSessionPipelineJobStatus.Dead]: 'Dead',
  [CanvassingSessionPipelineJobStatus.Done]: 'Done',
  [CanvassingSessionPipelineJobStatus.FailedLegacy]: 'Failed (legacy)',
  [CanvassingSessionPipelineJobStatus.Leased]: 'Leased',
  [CanvassingSessionPipelineJobStatus.Queued]: 'Queued',
  [CanvassingSessionPipelineJobStatus.Unknown]: 'Unknown',
};

const doorVisitStatusLabels: Record<CanvassingSessionDoorVisitStatus, string> = {
  [CanvassingSessionDoorVisitStatus.NoSpeech]: 'No speech',
  [CanvassingSessionDoorVisitStatus.Pending]: 'Pending',
  [CanvassingSessionDoorVisitStatus.SubmittedP1]: 'Submitted pass 1',
  [CanvassingSessionDoorVisitStatus.SubmittedP2]: 'Submitted pass 2',
  [CanvassingSessionDoorVisitStatus.SubmittingP1]: 'Submitting pass 1',
  [CanvassingSessionDoorVisitStatus.SubmittingP2]: 'Submitting pass 2',
  [CanvassingSessionDoorVisitStatus.Unknown]: 'Unknown',
};

const telemetryCategoryLabels: Record<SessionTelemetryCategory, string> = {
  [SessionTelemetryCategory.Audio]: 'Audio',
  [SessionTelemetryCategory.Connectivity]: 'Connectivity',
  [SessionTelemetryCategory.Gps]: 'GPS',
  [SessionTelemetryCategory.Heartbeat]: 'Heartbeat',
  [SessionTelemetryCategory.Recovery]: 'Recovery',
  [SessionTelemetryCategory.Session]: 'Session',
  [SessionTelemetryCategory.Synchronization]: 'Synchronization',
};

const telemetrySourceLabels: Record<SessionTelemetrySource, string> = {
  [SessionTelemetrySource.App]: 'Mobile app',
  [SessionTelemetrySource.Native]: 'Native runtime',
};

const telemetryOutcomeLabels: Record<SessionTelemetryOutcome, string> = {
  [SessionTelemetryOutcome.Failure]: 'Failure',
  [SessionTelemetryOutcome.Info]: 'Information',
  [SessionTelemetryOutcome.Success]: 'Success',
};

const telemetryEventTypeLabels: Record<SessionTelemetryEventType, string> = {
  [SessionTelemetryEventType.AudioCaptureVerified]: 'Audio capture verified',
  [SessionTelemetryEventType.AudioFileMissing]: 'Audio file missing',
  [SessionTelemetryEventType.AudioInterrupted]: 'Audio interrupted',
  [SessionTelemetryEventType.AudioInterruptionProlonged]: 'Audio interruption prolonged',
  [SessionTelemetryEventType.AudioRecorderStarted]: 'Audio recorder started',
  [SessionTelemetryEventType.AudioRecoveryAttempted]: 'Audio recovery attempted',
  [SessionTelemetryEventType.AudioRecoveryRetryFailed]: 'Audio recovery retry failed',
  [SessionTelemetryEventType.AudioResumed]: 'Audio resumed',
  [SessionTelemetryEventType.AudioS3PutSucceeded]: 'Audio S3 upload succeeded',
  [SessionTelemetryEventType.AudioSegmentCounterRecovered]: 'Audio segment counter recovered',
  [SessionTelemetryEventType.AudioSegmentFinalized]: 'Audio segment finalized',
  [SessionTelemetryEventType.AudioStartFailed]: 'Audio start failed',
  [SessionTelemetryEventType.AudioStartRequested]: 'Audio start requested',
  [SessionTelemetryEventType.AudioStoppedUnexpectedly]: 'Audio stopped unexpectedly',
  [SessionTelemetryEventType.AudioStopCompleted]: 'Audio stop completed',
  [SessionTelemetryEventType.AudioUploadAttempted]: 'Audio upload attempted',
  [SessionTelemetryEventType.AudioUploadConfirmed]: 'Audio upload confirmed',
  [SessionTelemetryEventType.AudioUploadFailed]: 'Audio upload failed',
  [SessionTelemetryEventType.CaptureTeardownCompleted]: 'Capture teardown completed',
  [SessionTelemetryEventType.CaptureTeardownStarted]: 'Capture teardown started',
  [SessionTelemetryEventType.ConnectivityChanged]: 'Connectivity changed',
  [SessionTelemetryEventType.GpsBatchFailed]: 'GPS batch failed',
  [SessionTelemetryEventType.GpsBatchSucceeded]: 'GPS batch succeeded',
  [SessionTelemetryEventType.GpsStarted]: 'GPS started',
  [SessionTelemetryEventType.GpsStopped]: 'GPS stopped',
  [SessionTelemetryEventType.GpsStopFailed]: 'GPS stop failed',
  [SessionTelemetryEventType.GpsStopRequested]: 'GPS stop requested',
  [SessionTelemetryEventType.Heartbeat]: 'Heartbeat',
  [SessionTelemetryEventType.RecoveryCompleted]: 'Recovery completed',
  [SessionTelemetryEventType.RecoveryFailed]: 'Recovery failed',
  [SessionTelemetryEventType.RecoveryStarted]: 'Recovery started',
  [SessionTelemetryEventType.SessionActive]: 'Session active',
  [SessionTelemetryEventType.SessionComplete]: 'Session complete',
  [SessionTelemetryEventType.SessionEndConfirmed]: 'Session end confirmed',
  [SessionTelemetryEventType.SessionEndQueued]: 'Session end queued',
  [SessionTelemetryEventType.SessionEndRequested]: 'Session end requested',
  [SessionTelemetryEventType.SessionServerBound]: 'Session server bound',
  [SessionTelemetryEventType.SessionStartRequested]: 'Session start requested',
  [SessionTelemetryEventType.SyncCompleted]: 'Synchronization completed',
  [SessionTelemetryEventType.SyncFailed]: 'Synchronization failed',
  [SessionTelemetryEventType.SyncStarted]: 'Synchronization started',
  [SessionTelemetryEventType.TelemetryFlushRecovered]: 'Telemetry flush recovered',
};

const telemetryAudioStateLabels: Record<SessionTelemetryAudioState, string> = {
  [SessionTelemetryAudioState.Failed]: 'Failed',
  [SessionTelemetryAudioState.Idle]: 'Idle',
  [SessionTelemetryAudioState.Interrupted]: 'Interrupted',
  [SessionTelemetryAudioState.Recording]: 'Recording',
  [SessionTelemetryAudioState.Starting]: 'Starting',
  [SessionTelemetryAudioState.Stopped]: 'Stopped',
  [SessionTelemetryAudioState.Stopping]: 'Stopping',
};

const telemetryGpsStateLabels: Record<SessionTelemetryGpsState, string> = {
  [SessionTelemetryGpsState.Active]: 'Active',
  [SessionTelemetryGpsState.Failed]: 'Failed',
  [SessionTelemetryGpsState.Idle]: 'Idle',
  [SessionTelemetryGpsState.Starting]: 'Starting',
  [SessionTelemetryGpsState.Stopped]: 'Stopped',
  [SessionTelemetryGpsState.Stopping]: 'Stopping',
};

const telemetryConnectivityLabels: Record<SessionTelemetryConnectivity, string> = {
  [SessionTelemetryConnectivity.Offline]: 'Offline',
  [SessionTelemetryConnectivity.Online]: 'Online',
  [SessionTelemetryConnectivity.Unknown]: 'Unknown',
};

const telemetryFailureReasonLabels: Record<SessionTelemetryFailureReason, string> = {
  [SessionTelemetryFailureReason.AudioFocusUnavailable]: 'Audio focus unavailable',
  [SessionTelemetryFailureReason.AudioSessionUnavailable]: 'Audio session unavailable',
  [SessionTelemetryFailureReason.ConfirmationFailed]: 'Confirmation failed',
  [SessionTelemetryFailureReason.DeferredJsUnavailable]: 'Deferred JavaScript unavailable',
  [SessionTelemetryFailureReason.FlushTimeout]: 'Flush timeout',
  [SessionTelemetryFailureReason.MetadataInvalid]: 'Metadata invalid',
  [SessionTelemetryFailureReason.PermissionDenied]: 'Permission denied',
  [SessionTelemetryFailureReason.PermissionRevoked]: 'Permission revoked',
  [SessionTelemetryFailureReason.RecorderEncodingFailed]: 'Recorder encoding failed',
  [SessionTelemetryFailureReason.RecorderInitFailed]: 'Recorder initialization failed',
  [SessionTelemetryFailureReason.S3PutFailed]: 'S3 PUT failed',
  [SessionTelemetryFailureReason.SigningFailed]: 'Signing failed',
  [SessionTelemetryFailureReason.StartTimeout]: 'Start timeout',
  [SessionTelemetryFailureReason.StopFailed]: 'Stop failed',
  [SessionTelemetryFailureReason.StorageUnavailable]: 'Storage unavailable',
  [SessionTelemetryFailureReason.UnknownNativeError]: 'Unknown native error',
  [SessionTelemetryFailureReason.UserEnd]: 'User ended',
};

const SAFE_SESSION_TELEMETRY_DETAIL_KEYS = new Set([
  'sessionState',
  'appLifecycle',
  'audioState',
  'lastSegmentNumber',
  'lastSegmentCapturedAt',
  'capturedSegmentCount',
  'pendingUploadCount',
  's3PutSuccessCount',
  'confirmedUploadCount',
  'lastUploadAttemptAt',
  'lastUploadSuccessAt',
  'lastUploadFailureStage',
  'gpsState',
  'lastGpsBatchAt',
  'lastGpsBatchPointCount',
  'connectivity',
  'telemetryQueueDepth',
  'appBuild',
  'appVersion',
  'osVersion',
  'platform',
  'inputPortType',
  'interruptionOptionShouldResume',
  'interruptionType',
  'outputPortTypes',
  'preferredInputPortType',
  'routeChangeReason',
  'captureGeneration',
  'outageId',
  'segmentId',
  'captureSequence',
  'segmentNumber',
  'previousNextSegmentNumber',
  'highestObservedSegmentNumber',
  'nextSegmentNumber',
  'attempt',
  'attemptsSinceLastReport',
  'metadataVersion',
  'elapsedMs',
  'durationMs',
  'interruptionDurationMs',
  'terminal',
  'captureState',
  'trigger',
  'recoveryMode',
  'failureReason',
  'teardownReason',
  'uploadStage',
]);

const isSafeSessionTelemetryDetailValue = (value: unknown): boolean =>
  typeof value === 'string' ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  typeof value === 'boolean' ||
  (Array.isArray(value) && value.every(entry => typeof entry === 'string'));

export const getCanvassingSessionCaptureStatusLabel = (status: CanvassingSessionCaptureStatus): string =>
  captureStatusLabels[status];

export const getCanvassingSessionCaptureStatusFilterLabel = (status: CanvassingSessionCaptureStatusFilter): string =>
  captureStatusFilterLabels[status];

export const getCanvassingSessionProcessingStatusLabel = (status: CanvassingSessionProcessingStatus): string =>
  processingStatusLabels[status];

export const getCanvassingSessionListSortLabel = (sort: CanvassingSessionListSort): string => listSortLabels[sort];

export const getCanvassingSessionActivityKindLabel = (kind: CanvassingSessionActivityKind): string =>
  activityKindLabels[kind];

export const getCanvassingSessionPipelineStageLabel = (stage: CanvassingSessionPipelineStage): string =>
  pipelineStageLabels[stage];

export const getCanvassingSessionPipelineJobStatusLabel = (status: CanvassingSessionPipelineJobStatus): string =>
  pipelineJobStatusLabels[status];

export const getCanvassingSessionDoorVisitStatusLabel = (status: CanvassingSessionDoorVisitStatus): string =>
  doorVisitStatusLabels[status];

export const getSessionTelemetryCategoryLabel = (category: SessionTelemetryCategory): string =>
  telemetryCategoryLabels[category];

export const getSessionTelemetrySourceLabel = (source: SessionTelemetrySource): string => telemetrySourceLabels[source];

export const getSessionTelemetryOutcomeLabel = (outcome: SessionTelemetryOutcome): string => telemetryOutcomeLabels[outcome];

export const getSessionTelemetryEventTypeLabel = (type: SessionTelemetryEventType): string => telemetryEventTypeLabels[type];

export const getSessionTelemetryAudioStateLabel = (state: SessionTelemetryAudioState): string => telemetryAudioStateLabels[state];

export const getSessionTelemetryGpsStateLabel = (state: SessionTelemetryGpsState): string => telemetryGpsStateLabels[state];

export const getSessionTelemetryConnectivityLabel = (connectivity: SessionTelemetryConnectivity): string =>
  telemetryConnectivityLabels[connectivity];

export const getSessionTelemetryFailureReasonLabel = (reason: string): string => {
  const knownReason = Object.values(SessionTelemetryFailureReason).find(value => value === reason);
  return knownReason === undefined ? reason : telemetryFailureReasonLabels[knownReason];
};

export const formatSessionTelemetryDetails = (details: unknown): string => {
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return '';

  const safeDetails = Object.fromEntries(
    Object.entries(details).filter(
      ([key, value]) => SAFE_SESSION_TELEMETRY_DETAIL_KEYS.has(key) && value !== null && isSafeSessionTelemetryDetailValue(value),
    ),
  );
  const displayDetails = Object.fromEntries(Object.entries(safeDetails).map(([key, value]) => {
    if ((key === 'failureReason' || key === 'teardownReason') && typeof value === 'string') {
      return [key, getSessionTelemetryFailureReasonLabel(value)];
    }
    return [key, value];
  }));

  return Object.keys(displayDetails).length === 0 ? '' : JSON.stringify(displayDetails, null, 2);
};
